/**
 * Which card a paragraph in the document belongs to, for Sticky (a click in
 * the document scrolls the pane to that paragraph's card).
 *
 * Decided by text, the way a card's go-to finds its paragraph, not by the
 * stored paragraph index: the index comes from the analysis and goes stale as
 * soon as the document changes (a paragraph added above, a response applied),
 * and a stale index points at the neighbouring card.
 */
import {
  isAcceptableParagraphSearchPick,
  scoreParagraphAgainstSearch,
} from '../word/api/paragraphSearchMatch';
import type { CrossCheckClause } from '../types/crossCheck';
import {
  getClientRequestSearchText,
  getModuleBNavigationAnchor,
  getTableRowSearchAnchors,
  type CrossCheckFeatureContext,
} from './crossCheckDocumentUtils';

/**
 * The texts that identify a card's paragraph — the same ones the card's go-to
 * searches for (client request, Module B anchor, table-row cell anchors).
 */
export function stickySearchTextsForClause(
  clause: CrossCheckClause,
  ctx?: CrossCheckFeatureContext,
): string[] {
  const texts = [
    getClientRequestSearchText(clause, ctx),
    getModuleBNavigationAnchor(clause, ctx),
    ...getTableRowSearchAnchors(clause, ctx),
  ]
    .map((t) => (t || '').trim())
    .filter((t) => t.length > 0);
  return Array.from(new Set(texts));
}

export type StickyCardCandidate = {
  id: string;
  /** Normalized texts that identify the card's paragraph. */
  searchNorms: string[];
  /** Stored paragraph index; only breaks a tie between equally good text matches. */
  index: number | null;
};

export type StickyCardPick = { id: string; score: number };

/**
 * @param cursorNorms the cursor paragraph's normalized texts: as Word shows it
 *   (with tracked deletions) and with every change accepted. A card whose
 *   request has since been answered still matches the shown text.
 */
export function pickStickyCardByText(
  cursorNorms: string[],
  candidates: StickyCardCandidate[],
  cursorIndex: number | null,
): StickyCardPick | null {
  const paragraphs = cursorNorms.filter((t) => t && t.length > 0);
  if (paragraphs.length === 0) return null;

  let best: (StickyCardPick & { indexHit: boolean }) | null = null;
  for (const candidate of candidates) {
    let score = 0;
    for (const searchNorm of candidate.searchNorms) {
      if (!searchNorm) continue;
      for (const paraNorm of paragraphs) {
        const s = scoreParagraphAgainstSearch(paraNorm, searchNorm);
        if (s > score && isAcceptableParagraphSearchPick(s, searchNorm)) score = s;
      }
    }
    if (score <= 0) continue;
    const indexHit = cursorIndex != null && candidate.index === cursorIndex;
    if (
      !best ||
      score > best.score ||
      (score === best.score && indexHit && !best.indexHit)
    ) {
      best = { id: candidate.id, score, indexHit };
    }
  }
  return best ? { id: best.id, score: best.score } : null;
}
